import { useCallback, useContext } from 'react';

import { useStoreSelector } from '../../../core/store/selector.js';
import type { Point } from '../../core/types/point.js';
import {
  GanttChartStateContext,
  type GanttChartState,
  type GanttChartContent,
} from '../context/GanttChartStateContext.js';

export const useSetTooltipState = () => {
  const { setState } = useContext(GanttChartStateContext);

  const setTooltipStateContent = useCallback(
    (content: GanttChartContent | undefined) => {
      setState((prev) => {
        return {
          ...prev,
          tooltip: {
            ...prev.tooltip,
            content,
          },
        };
      });
    },
    [setState],
  );

  const setTooltipStatePosition = useCallback(
    (position: Point) => {
      setState((prev) => {
        return {
          ...prev,
          tooltip: {
            ...prev.tooltip,
            position,
          },
        };
      });
    },
    [setState],
  );

  const setTooltipStateVisibility = useCallback(
    (visible: boolean) => {
      setState((prev) => {
        return {
          ...prev,
          tooltip: {
            ...prev.tooltip,
            visible,
          },
        };
      });
    },
    [setState],
  );

  const setTooltipStatePinned = useCallback(
    (pinned: boolean) => {
      setState((prev) => {
        return {
          ...prev,
          tooltip: {
            ...prev.tooltip,
            pinned,
          },
        };
      });
    },
    [setState],
  );

  return {
    setTooltipStateContent,
    setTooltipStatePosition,
    setTooltipStateVisibility,
    setTooltipStatePinned,
  };
};

const useSelector = <T>(selector: (state: GanttChartState) => T): T =>
  useStoreSelector(GanttChartStateContext, selector);

export const useTooltipContent = () =>
  useSelector((state: GanttChartState) => state.tooltip.content);

export const useTooltipPinnedStatus = () =>
  useSelector((state: GanttChartState) => state.tooltip.pinned);

export const useTooltipPosition = () =>
  useSelector((state: GanttChartState) => state.tooltip.position);

export const useTooltipVisibility = () =>
  useSelector((state: GanttChartState) => state.tooltip.visible);
